// eslint-disable-next-line no-underscore-dangle
export default function _tabGroup() {
    /**
     * @const {string} - The selector used to find the tab group container
     */
    const TAB_GROUP_SELECTOR = '.tabs';

    /**
     * @const {string} - The selector to find the tab navigation items
     */
    const TAB_NAVIGATION_SELECTOR_BUTTON = '[data-tab-group-control]';

    /**
     * @const {string} - The active class for the content container
     */
    const TAB_CONTENT_ACTIVE_CLASS = 'tab__content--active';

    /**
     * @const {string} - The active class for the tab button
     */
    const TAB_BUTTON_ACTIVE_CLASS = 'tab__button--active';

    /**
     * @const {string} - The selector to find content of the tab group
     */
    const TAB_CONTENT_SELECTOR = '[data-tab-group-controlled-by]';

    /**
     * The Tab group class
     * @param {HTMLElement} - Passed the html element for a tab group
     * @class
     */
    class TabGroup {
        constructor(tabGroup) {
            // The tab group HTML element
            this.tabGroup = tabGroup;

            // Bind the required functions to the tabGroup class
            this.showContent = this.showContent.bind(this);

            // Get the tab group navigation buttons
            this.navigationButtons = this.tabGroup.querySelectorAll(
                TAB_NAVIGATION_SELECTOR_BUTTON
            );

            // Destroy any previously set event listeners before initializing new ones
            this.removeEventListeners();

            // Add the event listeners
            this.addEventListeners();

            // Get the location hash
            const locationHash = window.location.hash.replace('#', '');

            // Check that we got something back from the url
            if (locationHash) {
                // Open the content
                this.openTab(locationHash);
            }

            // Get the content areas for the tabs
            this.contentContainers =
                this.tabGroup.querySelectorAll(TAB_CONTENT_SELECTOR);
        }

        /**
         * Function that adds the event listeners when the tab group is initialized
         * @method
         */
        addEventListeners() {
            // Add the event listener to change the content when a button is clicked
            this.navigationButtons.forEach((navigationButton) => {
                navigationButton.addEventListener('click', this.showContent);
            });
        }

        /**
         * Function that removes the event listeners that are setup in the add event listeners function
         * @method
         */
        removeEventListeners() {
            // Remove the event listener that shows content when a button is clicked
            this.navigationButtons.forEach((navigationButton) => {
                navigationButton.removeEventListener('click', this.showContent);
            });
        }

        /**
         * Removes the active class from all the content containers
         * @method
         */
        removeActiveClassFromContent() {
            this.contentContainers.forEach((contentContainer) => {
                contentContainer.classList.remove(TAB_CONTENT_ACTIVE_CLASS);
            });
        }

        /**
         * Removes the active class from all the navigation buttons
         * @method
         */
        removeActiveClassFromButtons() {
            this.navigationButtons.forEach((button) => {
                button.classList.remove(TAB_BUTTON_ACTIVE_CLASS);
            });
        }

        /**
         * Open a specific tab (Based on data controlled by attribute)
         * @method
         * @param {string} tabToOpen - The string to define the tab to open
         */
        openTab(tabToOpen) {
            // get the content to display based on the passed variable (note that the array from is needed as find is not a method on node lists)
            const contentToDisplay = Array.from(this.contentContainers).find(
                (contentContainer) =>
                    contentContainer.dataset.tabGroupControlledBy === tabToOpen
            );

            // Get the tab for the current active content
            const activeTab = Array.from(this.navigationButtons).find(
                (button) => button.dataset.tabGroupControl === tabToOpen
            );

            // If we found the content to display
            if (contentToDisplay && activeTab) {
                // Remove the active class from all content
                this.removeActiveClassFromContent();
                // Remove the active class from all buttons
                this.removeActiveClassFromButtons();

                // Add the active class to the content object
                contentToDisplay.classList.add(TAB_CONTENT_ACTIVE_CLASS);
                // Add the active class to the tab
                activeTab.classList.add(TAB_BUTTON_ACTIVE_CLASS);
            }
        }

        /**
         * Adds the active class to the content area
         * @param {event} event - The firing event (This should be fired by a element with the data attribute data-tab-group-control that controls )
         * @method
         */
        showContent(event) {
            this.navigationButtons.forEach((buttons) => {
                buttons.setAttribute('aria-selected', 'false');
            });

            event.srcElement.setAttribute('aria-selected', 'true');
            // Open the specific tab
            this.openTab(event.target.dataset.tabGroupControl);
            this.closeOverflowMenu();
        }
    }

    /**
     * Function that initializes the tab groups on the page
     */
    function initializeTabGroups() {
        const tabGroupInstances = document.querySelectorAll(TAB_GROUP_SELECTOR);

        // Go through each tab group instance and create a new class from that tabGroup
        tabGroupInstances.forEach((tabGroup) => {
            // Create a new item to modify
            const DOMItem = tabGroup;

            DOMItem.tabGroup = new TabGroup(tabGroup);
        });
    }

    /**
     * @const {boolean}
     */
    const tabGroupExists = !!document.querySelector(TAB_GROUP_SELECTOR);

    // If the module exists, do whatever we need to
    if (tabGroupExists) {
        initializeTabGroups();
    }
}

_tabGroup();
